import { ShipInfo } from '@ais-ui/websockets';
import { useGetShipByMmsi } from '@ais-ui/api';
import {
  Button,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
  useIsMobile,
} from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { XIcon } from 'lucide-react';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import React from 'react';
import { VesselResult } from '@ais-ui/api-spec/shipdb';
import { extractMmsiFromShipInfo } from '../../utils/extractMmsiFromShipInfo';
import { ErrorDisplay } from '@ais-ui/ui-core';
import { usePermissions } from '../../Permissions/PermissionsProvider';

interface InspectionsProps {
  inspections: any;
}

interface BansProps {
  bans: any;
}

interface SanctionsProps {
  sanctions: any;
}

interface InspectionItemProps {
  label: string;
  value: string;
}

interface BanItemProps {
  label: string;
  value: string;
}

interface SanctionItemProps {
  label: string;
  value: string;
}

const InspectionItem = ({ label, value }: InspectionItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const Inspection = ({ inspection }: any) => {
  const { messages } = useLocalization();
  const { formatDateWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <InspectionItem
        label={messages.shipDetailsIntegrityInspectionsDateLabel}
        value={formatDateWithTimeZone(inspection?.inspection_date)}
      />
      <InspectionItem
        label={messages.shipDetailsIntegrityInspectionsDetentionDatesLabel}
        value={
          formatDateWithTimeZone(inspection?.detention_begin_date)
            ? formatDateWithTimeZone(inspection?.detention_end_date)
              ? `${formatDateWithTimeZone(
                  inspection?.detention_begin_date,
                )} - ${formatDateWithTimeZone(inspection?.detention_end_date)}`
              : `${formatDateWithTimeZone(
                  inspection?.detention_begin_date,
                )} - ${
                  messages.shipDetailsIntegrityInspectionsDetentionDatesOngoing
                }`
            : messages.shipDetailsIntegrityInspectionsDetentionDatesEmpty
        }
      />
      <InspectionItem
        label={messages.shipDetailsIntegrityInspectionsPortLabel}
        value={inspection?.inspection_port}
      />
      <InspectionItem
        label={messages.shipDetailsIntegrityInspectionsCountryLabel}
        value={inspection?.inspection_country}
      />
      <InspectionItem
        label={messages.shipDetailsIntegrityInspectionsRelatedPSCLabel}
        value={inspection?.related_psc}
      />
      <InspectionItem
        label={messages.shipDetailsIntegrityInspectionsTypeLabel}
        value={inspection?.inspection_type || '-'}
      />
      <InspectionItem
        label={messages.shipDetailsIntegrityInspectionsDeficienciesLabel}
        value={inspection?.deficiency_count}
      />
      <InspectionItem
        label={messages.shipDetailsIntegrityInspectionsDetainedLabel}
        value={inspection?.is_detained ? 'Yes' : 'No'}
      />
      <InspectionItem
        label={messages.shipDetailsIntegrityInspectionsResultLabel}
        value={inspection?.inspection_result || '-'}
      />
      <InspectionItem
        label={messages.shipDetailsIntegrityInspectionsRepairYardLabel}
        value={
          inspection?.repair_yard || inspection?.repair_yard_country
            ? `${inspection?.repair_yard} ${inspection?.repair_yard_country}`
            : messages.integrityNoRepairs
        }
      />
    </div>
  );
};

const Inspections = ({ inspections }: InspectionsProps) => {
  const { messages } = useLocalization();

  if (!inspections || inspections.length === 0) {
    return (
      <div className="flex justify-center p-1">
        <p className="text-muted-foreground">
          {messages.shipDetailsIntegrityInspectionsEmptyLabel}
        </p>
      </div>
    );
  }

  return <Inspection inspection={inspections[0]}></Inspection>;
};

const BanItem = ({ label, value }: BanItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? String(value).replace(/_/g, ' ') : '-'}
      </span>
    </div>
  );
};

const Ban = ({ ban }: any) => {
  const { messages } = useLocalization();
  const { formatDateWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <BanItem
        label={messages.shipDetailsIntegrityBanDatesLabel}
        value={
          formatDateWithTimeZone(ban?.ban_begin_date)
            ? formatDateWithTimeZone(ban?.ban_end_date)
              ? `${formatDateWithTimeZone(
                  ban?.ban_begin_date,
                )} - ${formatDateWithTimeZone(ban?.ban_end_date)}`
              : `${formatDateWithTimeZone(ban?.ban_begin_date)} - ${
                  messages.shipDetailsIntegrityBanDatesOngoing
                }`
            : messages.shipDetailsIntegrityBanDatesEmpty
        }
      ></BanItem>
      <BanItem
        label={messages.shipDetailsIntegrityBanCountryOfInspectionLabel}
        value={ban?.ban_country_of_inspection}
      ></BanItem>
      <BanItem
        label={messages.shipDetailsIntegrityBanReasonLabel}
        value={ban?.ban_reason}
      ></BanItem>
      <BanItem
        label={messages.shipDetailsIntegrityBanRelatedPSCLabel}
        value={ban?.related_psc}
      ></BanItem>
      <BanItem
        label={messages.shipDetailsIntegrityBanStatusLabel}
        value={ban?.ban_status}
      ></BanItem>
    </div>
  );
};

const Bans = ({ bans }: BansProps) => {
  const { messages } = useLocalization();

  if (!bans || bans.length === 0) {
    return (
      <div className="flex justify-center p-1">
        <p className="text-muted-foreground">
          {messages.shipDetailsIntegrityBansEmptyLabel}
        </p>
      </div>
    );
  }

  return <Ban ban={bans[0]}></Ban>;
};

const SanctionItem = ({ label, value }: SanctionItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? String(value).replace(/_/g, ' ') : '-'}
      </span>
    </div>
  );
};

const Sanction = ({ sanction }: any) => {
  const { messages } = useLocalization();
  const { formatDateWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <SanctionItem
        label={messages.shipDetailsIntegritySanctionDatesLabel}
        value={
          formatDateWithTimeZone(sanction?.sanction_begin_date)
            ? formatDateWithTimeZone(sanction?.sanction_end_date)
              ? `${formatDateWithTimeZone(
                  sanction?.sanction_begin_date,
                )} - ${formatDateWithTimeZone(sanction?.sanction_end_date)}`
              : `${formatDateWithTimeZone(sanction?.sanction_begin_date)} - ${
                  messages.shipDetailsIntegritySanctionDatesOngoing
                }`
            : messages.shipDetailsIntegritySanctionDatesEmpty
        }
      ></SanctionItem>
      <SanctionItem
        label={messages.shipDetailsIntegritySanctionByLabel}
        value={sanction?.sanction_by}
      ></SanctionItem>
      <SanctionItem
        label={messages.shipDetailsIntegritySanctionProgramLabel}
        value={sanction?.program}
      ></SanctionItem>
      <SanctionItem
        label={messages.shipDetailsIntegritySanctionReasonLabel}
        value={sanction?.reason}
      ></SanctionItem>
      <SanctionItem
        label={messages.shipDetailsIntegritySanctionTypeLabel}
        value={sanction?.sanction_type}
      ></SanctionItem>
    </div>
  );
};

const Sanctions = ({ sanctions }: SanctionsProps) => {
  const { messages } = useLocalization();

  if (!sanctions || sanctions.length === 0) {
    return (
      <div className="flex justify-center p-1">
        <p className="text-muted-foreground">
          {messages.shipDetailsIntegritySanctionEmptyLabel}
        </p>
      </div>
    );
  }

  return <Sanction sanction={sanctions[0]}></Sanction>;
};

const AllInspections = ({ inspections }: InspectionsProps) => {
  const { messages } = useLocalization();
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          disabled={!inspections || inspections.length === 0}
          className="text-sm font-medium underline underline-offset-4"
        >
          {messages.shipDetailsIntegrityInspectionsViewAllButtonText}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        className="flex flex-col space-y-4 min-w-[420px]"
      >
        <div className="flex justify-between">
          <div className="text-lg font-medium flex-none">Inspections</div>
          <Button
            variant="ghost"
            size="icon"
            className="-mt-3 -mr-3"
            onClick={() => setOpen(false)}
          >
            <XIcon className="size-4" />
          </Button>
        </div>
        <Separator></Separator>
        <ScrollArea className="h-96 p-0 overflow-y-auto rounded-lg">
          {inspections?.map((inspection: any, index: number) => (
            <div key={index} className="space-y-4 mb-8 pr-4">
              <div className="flex flex-col space-y-1">
                <p className="text-md font-medium flex-none">
                  {messages.shipDetailsIntegrityInspectionsListLabel}{' '}
                  {index + 1}
                </p>
                <Separator></Separator>
              </div>
              <Inspection inspection={inspection}></Inspection>
            </div>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

const AllSanctions = ({ sanctions }: SanctionsProps) => {
  const { messages } = useLocalization();
  const [open, setOpen] = React.useState(false);
  if (!sanctions || sanctions.length === 0) {
    sanctions = [];
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          disabled={!sanctions || sanctions.length === 0}
          className="text-sm font-medium underline underline-offset-4"
        >
          {messages.shipDetailsIntegritySanctionsViewAllButtonText}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        className="flex flex-col space-y-4 min-w-[420px]"
      >
        <div className="flex justify-between">
          <div className="text-lg font-medium flex-none">Sanctions</div>
          <Button
            variant="ghost"
            size="icon"
            className="-mt-3 -mr-3"
            onClick={() => setOpen(false)}
          >
            <XIcon className="size-4" />
          </Button>
        </div>
        <Separator></Separator>
        <ScrollArea className="h-96 p-0 overflow-y-auto rounded-lg">
          {sanctions.map((sanction: any, index: number) => (
            <div key={index} className="space-y-4 mb-8 pr-4">
              <div className="flex flex-col space-y-1">
                <p className="text-md font-medium flex-none">
                  {messages.shipDetailsIntegritySanctionsListLabel} {index + 1}
                </p>
                <Separator></Separator>
              </div>

              <Sanction sanction={sanction}></Sanction>
            </div>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

const AllBans = ({ bans }: BansProps) => {
  const { messages } = useLocalization();
  const [open, setOpen] = React.useState(false);
  if (!bans || bans.length === 0) {
    bans = [];
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          disabled={!bans || bans.length === 0}
          className="text-sm font-medium underline underline-offset-4"
        >
          {messages.shipDetailsIntegrityBansViewAllButtonText}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        className="flex flex-col space-y-4 min-w-[420px]"
      >
        <div className="flex justify-between">
          <div className="text-lg font-medium flex-none">Bans</div>
          <Button
            variant="ghost"
            size="icon"
            className="-mt-3 -mr-3"
            onClick={() => setOpen(false)}
          >
            <XIcon className="size-4" />
          </Button>
        </div>
        <Separator></Separator>
        <ScrollArea className="h-96 p-0 overflow-y-auto rounded-lg">
          {bans.map((ban: any, index: number) => (
            <div key={index} className="space-y-4 mb-8 pr-4">
              <div className="flex flex-col space-y-1">
                <p className="text-md font-medium flex-none">
                  {messages.shipDetailsIntegrityBansListLabel} {index + 1}
                </p>
                <Separator></Separator>
              </div>

              <Ban ban={ban}></Ban>
            </div>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

interface IntegrityInformationProps {
  shipInfo: ShipInfo;
}

interface ExtendedVesselIntegrityInformationProps {
  extendedVesselInfo: VesselResult;
}

export const ExtendedVesselIntegrityInformation = ({
  extendedVesselInfo,
}: ExtendedVesselIntegrityInformationProps) => {
  const { messages } = useLocalization();
  const isMobile = useIsMobile();

  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium">
              {messages.shipDetailsIntegrityInspectionsTitle}
            </h3>
            {!isMobile && (
              <AllInspections
                inspections={extendedVesselInfo?.advancedIntegrity?.inspections}
              ></AllInspections>
            )}
          </div>
          <Separator className="mt-2"></Separator>
        </div>

        <Inspections
          inspections={extendedVesselInfo?.advancedIntegrity?.inspections}
        ></Inspections>
      </div>
      <div className="grid gap-1">
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium">
              {messages.shipDetailsIntegrityBansTitle}
            </h3>
            {!isMobile && (
              <AllBans
                bans={extendedVesselInfo?.advancedIntegrity?.bans}
              ></AllBans>
            )}
          </div>
          <Separator className="mt-2"></Separator>
        </div>
        <Bans bans={extendedVesselInfo?.advancedIntegrity?.bans}></Bans>
      </div>
      <div className="grid gap-1">
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium">
              {messages.shipDetailsIntegritySanctionsTitle}
            </h3>
            {!isMobile && (
              <AllSanctions
                sanctions={extendedVesselInfo?.advancedIntegrity?.sanctions}
              ></AllSanctions>
            )}
          </div>
          <Separator className="mt-2"></Separator>
        </div>
        <Sanctions
          sanctions={extendedVesselInfo?.advancedIntegrity?.sanctions}
        ></Sanctions>
      </div>
      <div className="grid gap-1">
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium">
              {messages.shipDetailsIntegrityOtherTitle}
            </h3>
          </div>
          <Separator className="mt-2"></Separator>
        </div>
        <div className="grid gap-1">
          <div className="grid grid-cols-[1fr_1fr] items-center gap-1">
            <div className="grid gap-1">
              <p className="text-muted-foreground text-xs">
                {messages.shipDetailsIntegrityOtherFlagPerformanceLabelParis}
              </p>
              <p className="text-xs">
                {extendedVesselInfo?.advancedIntegrity?.flagPerformanceParis
                  ? extendedVesselInfo.advancedIntegrity.flagPerformanceParis
                  : '-'}
              </p>
            </div>

            <div className="grid gap-1">
              <p className="text-muted-foreground text-xs">
                {messages.shipDetailsIntegrityOtherFlagPerformanceLabelTokyo}
              </p>
              <p className="text-xs">
                {extendedVesselInfo?.advancedIntegrity?.flagPerformanceTokyo
                  ? extendedVesselInfo.advancedIntegrity.flagPerformanceTokyo
                  : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const IntegrityInformation = ({
  shipInfo,
}: IntegrityInformationProps) => {
  const permissions = usePermissions();

  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  const {
    data: extendedVesselInfo,
    isLoading,
    isError,
  } = useGetShipByMmsi(extractMmsiFromShipInfo(shipInfo), hasShipDbPermissions);

  if (isLoading) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  if (isError) {
    return <ErrorDisplay className="h-full pt-60"></ErrorDisplay>;
  }

  if (!extendedVesselInfo) {
    return <ErrorDisplay className="h-full pt-60"></ErrorDisplay>;
  }

  return (
    <ExtendedVesselIntegrityInformation
      extendedVesselInfo={extendedVesselInfo}
    ></ExtendedVesselIntegrityInformation>
  );
};
